import { DocumentType } from '@/types/document';
import type { FundTransactionType } from '@/types/fund-transactions';

export interface DocumentProof {
  acceptedTags: ReadonlyArray<DocumentType>;
  documentTypesHint: string;
  contextOnlyTagHint: string;
  requiredDescription: string;
  optionalDescription: string;
  /** Sentence-start noun for the document, e.g. in "X must be verified". */
  noun: string;
  awaitingText: string;
  emptyText: string;
}

export type DocumentProofTransactionType = Extract<
  FundTransactionType,
  'FUND_COST' | 'DEAL_COST' | 'LP_TRANSFER'
>;

const INVOICE_PROOF: DocumentProof = {
  acceptedTags: [DocumentType.INVOICE],
  documentTypesHint:
    'Tag the invoice as Invoice. One document can carry several types.',
  contextOnlyTagHint:
    'Only the Invoice tag verifies the document source; other tags attach it for context.',
  requiredDescription:
    'The invoice behind this payment. Its type tag verifies the document source.',
  optionalDescription:
    'The invoice behind this payment. Optional, and can be attached after posting.',
  noun: 'The invoice',
  awaitingText: 'Awaiting the invoice. Attach it to verify this source.',
  emptyText: 'No invoice attached yet.',
};

const TRANSFER_AGREEMENT_PROOF: DocumentProof = {
  acceptedTags: [DocumentType.TRANSFER_AGREEMENT],
  documentTypesHint:
    'Tag the signed agreement as Transfer agreement. One document can carry several types.',
  contextOnlyTagHint:
    'Only the Transfer agreement tag verifies the document source; other tags attach it for context.',
  requiredDescription:
    'The signed transfer agreement. It is the only proof this transfer needs, and approval requires it.',
  optionalDescription: 'The signed transfer agreement.',
  noun: 'The signed transfer agreement',
  awaitingText:
    'Awaiting the signed transfer agreement. Attach it to verify this source.',
  emptyText: 'No transfer agreement attached yet.',
};

const DOCUMENT_PROOFS: Record<DocumentProofTransactionType, DocumentProof> = {
  FUND_COST: INVOICE_PROOF,
  DEAL_COST: INVOICE_PROOF,
  LP_TRANSFER: TRANSFER_AGREEMENT_PROOF,
};

export function hasDocumentProof(
  type: FundTransactionType
): type is DocumentProofTransactionType {
  return type in DOCUMENT_PROOFS;
}

export function documentProofFor(
  type: DocumentProofTransactionType
): DocumentProof {
  return DOCUMENT_PROOFS[type];
}
